/**
 * Promise-based wrapper over the Thrift WandService client (vision2 surface).
 *
 * One function per RPC. Type coercion lives in stores/services. Errors bubble
 * to the global pipeline; local catches must rethrow after setting UI state.
 */

// @ts-nocheck — generated Thrift JS uses untyped callbacks
import client from './client';
import { WandGetLayerEventsRequest } from '@helinks/wand-api-thrift/wand_types';
import { instancePoolFromStr } from '$lib/utils/enums';
import type {
  WandCodeBaseline,
  WandCodeChangeSet,
  WandCodeUpdate,
  WandEdgeEditResult,
  WandEdgeRemoveResult,
  WandExecutorContract,
  WandForkLayerRequest,
  WandGetNodePayloadRequest,
  WandGraphEdge,
  WandGraphNode,
  WandNodeSpec,
  WandLayer,
  WandLayerContent,
  WandLayerEvent,
  WandLayerMetaResult,
  WandLayerMetaUpdate,
  WandLayerSummary,
  WandListMyLayersRequest,
  WandListSpacesRequest,
  WandListSpacesResponse,
  WandListUsersRequest,
  WandListUsersResponse,
  WandNodeEditResult,
  WandNodePayloadMeta,
  WandNodeRemoveResult,
  WandNodeUIState,
  WandNodeUpdate,
  WandPermissions,
  WandPermissionTarget,
  WandPushCodeResult,
  WandPythonVenv,
  WandRunLayerRequest,
  WandRuntimeInstance,
  WandServiceSchema,
  WandSpace,
  WandUserInfo,
  WandTomlChange,
  WandTomlDocument,
  WandTomlSaveResult,
  WandTypedValue,
  WandValueInput,
} from '@helinks/wand-api-thrift/wand_types';
import type { WandStreamHandle } from '@helinks/wand-api-thrift/wand_kernel_types';

function promisify<T>(fn: (...args: any[]) => void, ...args: any[]): Promise<T> {
  return new Promise((resolve, reject) => {
    fn.call(client, ...args, (err: Error | null, res: T) => {
      err ? reject(err) : resolve(res);
    });
  });
}

// --- Schema & identity ---

export function listSchemas() {
  return promisify<WandServiceSchema[]>(client.listSchemas);
}

export function whoami() {
  return promisify<WandUserInfo>(client.whoami);
}

export function stsStatus() {
  return promisify<boolean>(client.stsStatus);
}

// --- Spaces ---

export function listSpaces(request: WandListSpacesRequest) {
  return promisify<WandListSpacesResponse>(client.listSpaces, request);
}

export function getSpace(spaceId: string) {
  return promisify<WandSpace>(client.getSpace, spaceId);
}

export function createSpace(title: string) {
  return promisify<WandSpace>(client.createSpace, title);
}

export function renameSpace(spaceId: string, title: string) {
  return promisify<WandSpace>(client.renameSpace, spaceId, title);
}

export function deleteSpace(spaceId: string) {
  return promisify<boolean>(client.deleteSpace, spaceId);
}

export function setSpacePermissions(
  spaceId: string,
  target: WandPermissionTarget,
  permissions: WandPermissions,
) {
  return promisify<WandSpace>(client.setSpacePermissions, spaceId, target, permissions);
}

// --- Layers ---

export function listLayersInSpace(spaceId: string) {
  return promisify<WandLayerSummary[]>(client.listLayersInSpace, spaceId);
}

export function listMyLayers(request: WandListMyLayersRequest) {
  return promisify<WandLayerSummary[]>(client.listMyLayers, request);
}

export function getLayer(layerId: string) {
  return promisify<WandLayer>(client.getLayer, layerId);
}

export function resolveNodes(nodes: WandNodeSpec[]) {
  return promisify<WandGraphNode[]>(client.resolveNodes, nodes);
}

export function createLayer(spaceId: string, content: WandLayerContent) {
  return promisify<WandLayer>(client.createLayer, spaceId, content);
}

export function saveLayer(layerId: string, expectedRevision: number, content: WandLayerContent) {
  return promisify<WandLayer>(client.saveLayer, layerId, expectedRevision, content);
}

export function getLayerToml(layerId: string, includeLayout = false) {
  return promisify<WandTomlDocument>(client.getLayerToml, layerId, includeLayout);
}

export function getNodeToml(layerId: string, nodeId: string, includeLayout = false) {
  return promisify<WandTomlDocument>(client.getNodeToml, layerId, nodeId, includeLayout);
}

export function previewLayerToml(layerId: string, text: string, editToken: string) {
  return promisify<WandTomlChange[]>(client.previewLayerToml, layerId, text, editToken);
}

export function previewNodeToml(layerId: string, nodeId: string, text: string, editToken: string) {
  return promisify<WandTomlChange[]>(client.previewNodeToml, layerId, nodeId, text, editToken);
}

export function saveLayerToml(layerId: string, text: string, editToken: string) {
  return promisify<WandTomlSaveResult>(client.saveLayerToml, layerId, text, editToken);
}

export function saveNodeToml(layerId: string, nodeId: string, text: string, editToken: string) {
  return promisify<WandTomlSaveResult>(client.saveNodeToml, layerId, nodeId, text, editToken);
}

export function deleteLayer(layerId: string) {
  return promisify<boolean>(client.deleteLayer, layerId);
}

export function deleteRunGroup(runGroupId: string) {
  return promisify<boolean>(client.deleteRunGroup, runGroupId);
}

export function setLayerPermissions(
  layerId: string,
  target: WandPermissionTarget,
  permissions: WandPermissions,
) {
  return promisify<WandLayer>(client.setLayerPermissions, layerId, target, permissions);
}

export function forkLayer(request: WandForkLayerRequest) {
  return promisify<WandLayer>(client.forkLayer, request);
}

// --- Granular layer edits ---
// Same revision semantics as saveLayer; each call validates the post-mutation
// graph and bumps contentRevision by 1. Failures throw WandValidationError
// with codes: stale_content, layer_running, not_found, port_has_incident_edges,
// conflict, plus validator-side codes (cycle, edge_type_mismatch, ...).

export function addNode(
  layerId: string, expectedRevision: number,
  node: WandNodeSpec, ui: WandNodeUIState,
) {
  return promisify<WandNodeEditResult>(client.addNode, layerId, expectedRevision, node, ui);
}

export function removeNode(layerId: string, expectedRevision: number, nodeId: string) {
  return promisify<WandNodeRemoveResult>(client.removeNode, layerId, expectedRevision, nodeId);
}

export function addEdge(layerId: string, expectedRevision: number, edge: WandGraphEdge) {
  return promisify<WandEdgeEditResult>(client.addEdge, layerId, expectedRevision, edge);
}

export function removeEdge(layerId: string, expectedRevision: number, edge: WandGraphEdge) {
  return promisify<WandEdgeRemoveResult>(client.removeEdge, layerId, expectedRevision, edge);
}

export function updateNode(
  layerId: string, expectedRevision: number, nodeId: string, patch: WandNodeUpdate,
) {
  return promisify<WandNodeEditResult>(client.updateNode, layerId, expectedRevision, nodeId, patch);
}

export function updateLayerMeta(
  layerId: string, expectedRevision: number, patch: WandLayerMetaUpdate,
) {
  return promisify<WandLayerMetaResult>(client.updateLayerMeta, layerId, expectedRevision, patch);
}


// --- Execution ---

export function runLayer(request: WandRunLayerRequest) {
  return promisify<WandLayer>(client.runLayer, request);
}

export function cancelLayer(layerId: string) {
  return promisify<boolean>(client.cancelLayer, layerId);
}

export function getLayerEvents(layerId: string, sinceCursor: string, blockMs: number) {
  const request = new WandGetLayerEventsRequest({
    layerId,
    sinceCursor: sinceCursor || undefined,
    blockMs,
  });
  return promisify<WandLayerEvent[]>(client.getLayerEvents, request);
}

export function listLayerPayloads(layerId: string) {
  return promisify<WandNodePayloadMeta[]>(client.listLayerPayloads, layerId);
}

export function getNodePayload(request: WandGetNodePayloadRequest) {
  return promisify<Record<string, WandTypedValue>>(client.getNodePayload, request);
}

export function encodeValues(values: WandValueInput[]) {
  return promisify<WandTypedValue[]>(client.encodeValues, values);
}

export function decodeValues(values: WandTypedValue[]) {
  return promisify<WandValueInput[]>(client.decodeValues, values);
}

// --- Code collab streams ---

export function getCodeBaseline(layerId: string, nodeId: string) {
  return promisify<WandCodeBaseline>(client.getCodeBaseline, layerId, nodeId);
}

export function pushCodeUpdates(
  layerId: string, nodeId: string, expectedVersion: number, changes: WandCodeChangeSet,
) {
  return promisify<WandPushCodeResult>(
    client.pushCodeUpdates, layerId, nodeId, expectedVersion, changes,
  );
}

export function subscribeCodeUpdates(layerId: string, nodeId: string) {
  return promisify<WandStreamHandle>(client.subscribeCodeUpdates, layerId, nodeId);
}

export function getCodeUpdatesSince(
  layerId: string, nodeId: string, sinceVersion: number,
) {
  return promisify<WandCodeUpdate[]>(
    client.getCodeUpdatesSince, layerId, nodeId, sinceVersion,
  );
}

// --- Executor contracts ---

export function listExecutorContracts() {
  return promisify<WandExecutorContract[]>(client.listExecutorContracts);
}

export function getExecutorContract(id: string) {
  return promisify<WandExecutorContract>(client.getExecutorContract, id);
}

export function ensureExecutorContract(thriftSource: string, serviceName: string) {
  return promisify<WandExecutorContract>(
    client.ensureExecutorContract, thriftSource, serviceName,
  );
}


// --- Runtime instances ---

export function listRuntimeInstances(runtimeType: string) {
  return promisify<WandRuntimeInstance[]>(
    client.listRuntimeInstances, instancePoolFromStr(runtimeType),
  );
}

export function getRuntimeInstance(id: string) {
  return promisify<WandRuntimeInstance>(client.getRuntimeInstance, id);
}

export function addRuntimeInstance(
  runtimeType: string, name: string, configJson: string, contractId: string,
) {
  return promisify<WandRuntimeInstance>(
    client.addRuntimeInstance, instancePoolFromStr(runtimeType), name, configJson, contractId,
  );
}

export function editRuntimeInstance(id: string, name: string, configJson: string) {
  return promisify<WandRuntimeInstance>(
    client.editRuntimeInstance, id, name, configJson,
  );
}

export function renameRuntimeInstance(id: string, name: string) {
  return promisify<WandRuntimeInstance>(client.renameRuntimeInstance, id, name);
}

export function deleteRuntimeInstance(id: string) {
  return promisify<boolean>(client.deleteRuntimeInstance, id);
}

export function listPythonVenvs() {
  return promisify<WandPythonVenv[]>(client.listPythonVenvs);
}

// --- Admin ---

export function listUsers(request: WandListUsersRequest) {
  return promisify<WandListUsersResponse>(client.listUsers, request);
}
